import { useState } from 'react';
import { formatMoneyInput, parseMoneyInput } from './money';

interface UseMoneyFieldOptions {
  /** Valor em número. `null` deixa o campo vazio (campo opcional). */
  value: number | null;
  /** Recebe `null` quando o usuário esvazia o campo. */
  onValueChange: (value: number | null) => void;
  /** Chamado quando casas decimais além de duas foram descartadas. */
  onTrimmed?: (trimmed: boolean) => void;
}

interface MoneyFieldProps {
  type: 'text';
  inputMode: 'decimal';
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Props para um `<input>` monetário, sem opinião sobre a casca visual —
 * o wizard do cardápio desenha o `R$` numa moldura própria, as configurações
 * usam o `Input` do painel e o backoffice usa a classe `.field`; os três
 * precisam do mesmo comportamento.
 *
 * Enquanto o campo está em foco vale o texto cru que o usuário digita; ao
 * sair do foco o texto volta a ser derivado do número, sempre com duas casas.
 * Sem isso `12.5` era exibido como `12,5`, divergindo do `R$ 12,50` que a
 * lista do cardápio e a prévia do cliente mostram para o mesmo valor.
 */
export function useMoneyField({
  value,
  onValueChange,
  onTrimmed,
}: UseMoneyFieldOptions): MoneyFieldProps {
  const [draft, setDraft] = useState<string | null>(null);

  return {
    type: 'text',
    inputMode: 'decimal',
    value: draft ?? formatMoneyInput(value),
    onChange: (event) => {
      const raw = event.target.value;
      setDraft(raw);

      if (raw.trim() === '') {
        onTrimmed?.(false);
        onValueChange(null);
        return;
      }

      const { value: parsed, trimmed } = parseMoneyInput(raw);
      onTrimmed?.(trimmed);
      onValueChange(parsed);
    },
    onFocus: () => setDraft(formatMoneyInput(value)),
    onBlur: () => setDraft(null),
  };
}
